'use strict'

import FileItem from './fileitem.coffee'
import FolderItem from './folderItem.coffee'
import TransferItem from './transferitem.coffee'
import { $log } from '../helpers/angular-migration-helpers'

class Transfer
  constructor: (serverTransfer) ->
    @from = null # Sender of the transfer
    @message = null # Message that is sent with the transfer
    @id = null # Identifier of the transfer (assigned by the back-end)
    @url = null # Direct download URL (assigned by the back-end)
    @availableUntil = null # Date until this transfer is available (assigned by the back-end)
    @nextFileId = 0 # File identifier of the next file that can be added
    @isEncrypted = false # Flag indicating whether this transfer is encrypted
    @totalDownloads = 0 # Number of completed downloads

    # Set the default options
    @options =
      region: null # TODO: Obtain the selected region
      shareMethod: 'email' # 'email', 'twitter', 'facebook', 'link'
      fromEmail: null # From address (required)
      message: null

    # Set status flags
    @status =
      isDownloaded: false
      isExpired: false
      isAvailable: false
      isPending: false

    # If a server transfer is specified, then we'll load it immediately
    if serverTransfer
      @update serverTransfer
    else
      # The 'files' and 'folders' objects contain all the files and folder in the transfer object. The objects
      # server as dictionaries and the (numerical) identifier serves as the key
      @files = {}
      @folders = {}

      # The root folder contains the root items. The folder itself isn't saved and is not listed in the 'folders' object
      @rootFolder = new FolderItem()

  update: (serverTransfer) ->
    # Keep the current file list (so we can check if files have been selected)
    oldFiles = @files
    oldSelectedFolder =
      # Save identifier (key)
      @id = serverTransfer.id
    @createdAt = new Date serverTransfer.createdAt
    @transfer = serverTransfer.transfer
    @availableUntil = new Date serverTransfer.availableUntil
    @totalDownloads = serverTransfer.totalDownloads

    # Save the sender and message
    @options.fromEmail = serverTransfer.from
    @options.message = serverTransfer.message

    # Copy the region
    @options.region = serverTransfer.region

    # Set sharing option
    switch serverTransfer.type
      when 'EMAIL'
        @options.shareMethod = 'email'
      when 'FACEBOOK'
        @options.shareMethod = 'facebook'
      when 'TWITTER'
        @options.shareMethod = 'twitter'
      when 'PUBLIC_LINK'
        @options.shareMethod = 'link'

    # Set status flags
    @status =
      isDownloaded: serverTransfer.totalDownloads > 0
      isExpired: serverTransfer.status is 'EXPIRED'
      isAvailable: serverTransfer.status is 'AVAILABLE'
      isDefective: serverTransfer.status is 'DEFECTIVE'
      isCancelled: serverTransfer.status is 'CANCELLED'
      isPending: serverTransfer.isPending
      isEncrypted: serverTransfer.encrypted

    # Save domain and direct download url
    @background = serverTransfer.background
    @url = serverTransfer.url

    # Check if the transfer is encrypted
    if serverTransfer.files
      # Don't use file-count and total-size
      delete @fileCount
      delete @totalSize

      # Reset the old files and folders
      @files = {}
      @folders = {}
      @rootFolder = new FolderItem()

      # Try to find the highest file identifier
      highestFileId = -1

      # Iterate through all the files
      for serverTransferItem in serverTransfer.files
        # Strip trailing '/' from folders
        if (
          serverTransferItem.isFolder &&
          serverTransferItem.name[serverTransferItem.name.length - 1] == '/'
        )
          serverTransferItem.name = serverTransferItem.name.substr(
            0
            serverTransferItem.name.length - 1
          )

        # Determine id and folder id
        id = TransferItem.convertIdToNumber serverTransferItem.id
        folderId = if serverTransferItem.folderKey
          TransferItem.convertIdToNumber serverTransferItem.folderKey
        else
          null

        # Check if we have the highest file identifier
        highestFileId = id if highestFileId < id

        # Create the transfer items
        if serverTransferItem.isFolder
          # Create the folder item and add it to the folder map
          transferItem = new FolderItem id, serverTransferItem.name

          # Save in the folder map
          @folders[id] = transferItem
        else
          # Create the file item and add it to the file map
          transferItem = new FileItem(
            id
            serverTransferItem.name
            serverTransferItem.size
            serverTransferItem.type
            new Date serverTransferItem.lastModifiedDate * 1000
          )
          transferItem.urlPreview = serverTransferItem.urlPreview if (
            serverTransferItem.urlPreview
          )
          transferItem.isPending = true if !!serverTransferItem.isPending

          # Save in the file map
          @files[id] = transferItem

        # The transfer item is known in the back-end
        transferItem.backEndStatus = 'finished'

        # Determine the proper folder (if it has a folder identifier)
        if folderId?
          folder = @folders[folderId]

          # Issue a warning if the folder couldn't be found
          if !folder
            $log.warn(
              "(Transfer '#{@id}': File '#{
                transferItem.name
              }' is located in a non-existing folder (moving it to the root-folder)"
            )
            folder = @rootFolder

        # If the folder is not known, then use the root folder
        folder ?= @rootFolder

        # Add the item to the folder
        folder.addItem transferItem

      # Next file identifier should be one higher than the highest file id
      @nextFileId = highestFileId + 1

      # Select all the files that were selected in the previous run
      if oldFiles
        for id, fileInfo of oldFiles
          if fileInfo.isSelected
            @files[id]?.selectItem()
    else
      # Remove the existing files and folders
      delete @files
      delete @folders
      delete @rootFolder

      # Keep track of the file count and size
      @fileCount = serverTransfer.fileCount
      @totalSize = serverTransfer.size

    # Allow chaining
    this

  isConfirmed: ->
    true

  clearSelection: ->
    # Deselect the root item
    @rootFolder.deselectItem()

  getFolder: (folderKey) ->
    if folderKey != undefined then @folders[folderKey] else @rootFolder

  getFolderByPath: (folderPath) ->
    # TODO: Optimize this
    for key, folder of @folders
      if folder.getCompletePath() is folderPath
        return folder
    null

  getItems: (folder) ->
    # The '/' option actually means the root folder
    folder = @rootFolder if folder == '/'

    # Retrieve the sub items
    (folder || @rootFolder)?.subItems || []

  getSelectedItems: (minify) ->
    # Add all the directories that are completely selected
    selectedFolders = []
    for key, folder of @folders
      if (
        folder.getSelectedFileCount() > 0 and
        folder.getSelectedFileCount() == folder.getTotalFileCount()
      )
        selectedFolders.push folder

    # Add all selected files
    selectedFiles = []
    for key, file of @files
      selectedFiles.push file if file.isSelected

    # Return the minified keys
    @minify selectedFolders, selectedFiles if minify

    # Return the folders and files (sorted)
    selectedFolders
      .sort (f) -> f.id
      .concat selectedFiles.sort (f) -> f.id

  minify: (folders, files) ->
    # Remove all files that are already referenced by a folder
    fileIndex = 0
    while fileIndex < files.length
      file = files[fileIndex]
      remove = false
      parentFolder = file.folder
      while !remove and parentFolder
        remove = true if parentFolder in folders
        parentFolder = parentFolder.folder
      if remove
        files.splice fileIndex, 1
      else
        ++fileIndex

    # Remove all folders that are already referenced by a folder
    folderIndex = 0
    while folderIndex < folders.length
      folder = folders[folderIndex]
      remove = false
      parentFolder = folder.folder
      while !remove and parentFolder
        remove = true if parentFolder in folders
        parentFolder = parentFolder.folder
      if remove
        folders.splice folderIndex, 1
      else
        ++folderIndex

    null

  # Convenience methods
  getTotalFolderCount: -> if @folders? then Object.keys(@folders).length else 0
  getTotalFileCount: ->
    if @rootFolder then @rootFolder.getTotalFileCount() else @fileCount
  getTotalFileSize: ->
    if @rootFolder then @rootFolder.getTotalFileSize() else @totalSize
  getSelectedFileCount: ->
    if @rootFolder then @rootFolder.getSelectedFileCount() else 0
  getSelectedFileSize: ->
    if @rootFolder then @rootFolder.getSelectedFileSize() else 0
  getPendingFileCount: ->
    if @rootFolder then @rootFolder.getPendingFileCount() else 0
  getPendingFileSize: ->
    if @rootFolder then @rootFolder.getPendingFileSize() else 0

export default Transfer
